import _ from 'lodash'
import { memo, useCallback, useEffect, useState } from 'react'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'

import ErrorMessage from '@/components/formik/messages/ErrorMessage'

import AutocompleteInput, { AutocompleteInputProps, AutocompleteValue } from './AutocompleteInput'

type AutocompleteInputControllerProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<AutocompleteInputProps, 'onBlur' | 'onChange' | 'value'> & {
    disableHelperText?: boolean
  }

const MemoizedAutocompleteInput = memo(AutocompleteInput)

export default function AutocompleteInputController<T extends FieldValues>(props: AutocompleteInputControllerProps<T>) {
  const { control, multiple, name, onInputChange, options, disableHelperText, ...rest } = props
  const {
    field,
    fieldState: { error, isTouched },
    formState: { defaultValues },
  } = useController({ name, control })

  const [inputValue, setInputValue] = useState<string>('')
  const [value, setValue] = useState<AutocompleteValue>(multiple ? [] : null)

  const initialValue = _.get(defaultValues, field.name)

  useEffect(() => {
    if (initialValue === field.value && options.length) {
      setValue(
        multiple ? _.filter(options, (option) => _.includes(initialValue, option.value)) : _.find(options, (option) => option.value === field.value) || null
      )
    }
  }, [initialValue, field.value, multiple, options])

  useEffect(() => {
    if (value === null) setInputValue('')
  }, [value])

  const handleChange = useCallback(
    (newValue: AutocompleteValue) => {
      setValue(newValue)
      field.onChange(Array.isArray(newValue) ? _.map(newValue, 'value') : newValue?.value || '')
    },
    [field]
  )

  const handleInputChange = useCallback(
    (newInputValue: string, reason: string) => {
      setInputValue(newInputValue)
      if (reason === 'input') onInputChange?.(newInputValue, reason)
    },
    [onInputChange]
  )

  return (
    <MemoizedAutocompleteInput
      {...rest}
      error={!!error && isTouched}
      helperText={!!error && isTouched && !disableHelperText && <ErrorMessage error={error.message} />}
      inputValue={inputValue}
      multiple={multiple}
      name={field.name}
      options={options}
      onBlur={field.onBlur}
      onChange={handleChange}
      onInputChange={handleInputChange}
      value={value}
    />
  )
}
